import { type FC, memo } from "react";
import type { EventListGridItem } from "src/http/types";
import { BaseDateCard } from "src/website-apps/responsive/stubhub-performer/components/calendar/BaseDateCard";
import { DAY_OF_MONTH_TBA } from "src/website-apps/responsive/stubhub-performer/components/calendar/constants";
import { MlbDateCardFull } from "./MlbDateCardFull";
import { MlbDateCardPortion } from "./MlbDateCardPortion";
import { mlbDateCardPortionContainer } from "./styles";

interface Props {
  /** The day of the month to display. */
  dayOfMonth: number | null;
  /** List of events occurring on the day. */
  events: EventListGridItem[];
  /** Performer's categoryId. */
  categoryId: number;
  /** Flag to mark the card as representing the current day. */
  today: boolean;
  /** Handler to open notification modal for a specific event. */
  openNotificationHandler: (eventId: number) => void;
  /** Click event handler. */
  onClick: (event: EventListGridItem) => void;
  /** Mouse enter event handler. */
  onMouseEnter: (event: EventListGridItem) => void;
  /** Position index of this card in the calendar (only days with events are counted). */
  calendarIndex: number;
}

/**
 * MLB date card dispatcher. Selects one of three rendering paths:
 *
 * - 0 events → empty `BaseDateCard`
 * - 1 event → `MlbDateCardFull` (full presentation; owns its own per-event state)
 * - 2+ events → `BaseDateCard` wrapping per-event `MlbDateCardPortion` children;
 *   the parent tile is static and each portion owns its own click target
 *
 * No hooks are called at this level — that's deliberate so empty/multi-event
 * days don't fire analytics, ticket-availability, or merch-tag computations
 * for placeholder events.
 */
export const MlbDateCard: FC<Props> = memo(
  ({ dayOfMonth, categoryId, events, today, openNotificationHandler, onClick, onMouseEnter, calendarIndex }) => {
    const [firstEvent] = events;
    if (!firstEvent) {
      return <BaseDateCard dayOfMonth={dayOfMonth} today={today} />;
    }

    if (events.length === 1) {
      return (
        <MlbDateCardFull
          event={firstEvent}
          isHomeGame={firstEvent.eventMetadata?.sports?.homeTeamId === categoryId}
          dayOfMonth={dayOfMonth}
          today={today}
          openNotificationHandler={openNotificationHandler}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          calendarIndex={calendarIndex}
        />
      );
    }

    return (
      <BaseDateCard
        dayOfMonth={firstEvent.isDateConfirmed ? dayOfMonth : DAY_OF_MONTH_TBA}
        borderlessHeading
        today={today}
        ariaLabel={firstEvent.name}
      >
        <div className={mlbDateCardPortionContainer}>
          {events.map((event) => (
            <MlbDateCardPortion
              key={event.eventId}
              event={event}
              isHomeGame={event.eventMetadata?.sports?.homeTeamId === categoryId}
              openNotificationHandler={openNotificationHandler}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              calendarIndex={calendarIndex}
            />
          ))}
        </div>
      </BaseDateCard>
    );
  },
);
MlbDateCard.displayName = "MlbDateCard";
